/**
 * Model Browser — Browse and add OpenRouter models (BYOK feature).
 *
 * Fetches the full model list from OpenRouter's /api/v1/models endpoint
 * via the user's API key proxy, lets users search/filter, and add models
 * to their custom model list stored in Supabase.
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { getCustomModels, TIERS, type ModelEntry, type ModelTier } from "../models.ts";

const TIER_LABELS: Record<ModelTier, string> = Object.fromEntries(
  TIERS.map((t) => [t.id, t.label])
) as Record<ModelTier, string>;

/** Shape of an OpenRouter model from /api/v1/models */
interface OpenRouterModel {
  id: string;
  name: string;
  description?: string;
  context_length?: number;
  pricing?: {
    prompt?: string;   // cost per token as string
    completion?: string;
  };
  top_provider?: {
    max_completion_tokens?: number;
  };
  architecture?: {
    modality?: string;
    tokenizer?: string;
  };
}

/** Credit cost calculation: creditCost = max(1, round(estimatedMsgCost / 0.0055)), or 0 if truly free */
function calcCreditCost(model: OpenRouterModel): number {
  const promptCost = parseFloat(model.pricing?.prompt ?? "0");
  const completionCost = parseFloat(model.pricing?.completion ?? "0");
  // Estimate: ~800 input tokens + ~400 output tokens per message
  const estimatedMsgCost = promptCost * 800 + completionCost * 400;
  if (estimatedMsgCost === 0) return 0;
  return Math.max(1, Math.round(estimatedMsgCost / 0.0055));
}

function assignTier(creditCost: number): ModelTier {
  if (creditCost === 0) return "free";
  if (creditCost <= 1) return "lightweight";
  if (creditCost <= 8) return "normal";
  return "expert";
}

@customElement("katanoid-model-browser")
export class KatanoidModelBrowser extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  // OpenRouter model list is public — no key needed for browsing.
  @state() private models: OpenRouterModel[] = [];
  @state() private filteredModels: OpenRouterModel[] = [];
  @state() private searchQuery = "";
  @state() private tierFilter: ModelTier | "all" = "all";
  @state() private loading = true;
  @state() private error: string | null = null;
  @state() private addedIds: Set<string> = new Set();

  override connectedCallback(): void {
    super.connectedCallback();
    this.loadAddedModels();
    this.fetchModels();
  }

  private loadAddedModels(): void {
    const custom = getCustomModels();
    this.addedIds = new Set(custom.map((m) => m.id));
  }

  private async fetchModels(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const resp = await fetch("https://openrouter.ai/api/v1/models");

      if (!resp.ok) {
        throw new Error(`Failed to fetch models (${resp.status})`);
      }

      const data = await resp.json();
      this.models = (data.data ?? []) as OpenRouterModel[];
      this.applyFilter();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load models.";
    } finally {
      this.loading = false;
    }
  }

  private applyFilter(): void {
    const q = this.searchQuery.toLowerCase().trim();
    const tier = this.tierFilter;

    let results = this.models;

    if (q) {
      results = results.filter(
        (m) =>
          m.id.toLowerCase().includes(q) ||
          (m.name ?? "").toLowerCase().includes(q) ||
          (m.description ?? "").toLowerCase().includes(q)
      );
    }

    if (tier !== "all") {
      results = results.filter((m) => assignTier(calcCreditCost(m)) === tier);
    }

    this.filteredModels = results.slice(0, 100);
  }

  protected override render(): TemplateResult {
    return html`
      <div class="billing-backdrop" @click=${this.handleClose}></div>
      <div class="billing-modal" style="max-width: 700px; max-height: 80vh; display: flex; flex-direction: column;">
        <div class="billing-modal-header">
          <h2 class="billing-modal-title">Browse Models</h2>
          <button class="btn btn--ghost btn--sm" @click=${this.handleClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </button>
        </div>

        <div style="display: flex; flex-direction: column; gap: var(--space-3);">
          <input
            class="input"
            type="text"
            placeholder="Search models..."
            .value=${this.searchQuery}
            style="width: 100%; height: 40px; font-size: var(--text-sm);"
            @input=${(e: InputEvent) => {
              this.searchQuery = (e.target as HTMLInputElement).value;
              this.applyFilter();
            }}
          />
          <div class="theme-toggle" style="align-self: flex-start;">
            ${(["all", ...TIERS.map((t) => t.id)] as const).map(
              (id) => {
                const label = id === "all" ? "All" : TIER_LABELS[id as ModelTier];
                const active = this.tierFilter === id;
                return html`
                  <button
                    class="theme-toggle-btn ${active ? "theme-toggle-btn--active" : ""}"
                    style="padding: var(--space-1) var(--space-3); font-size: var(--text-xs);"
                    @click=${() => { this.tierFilter = id as ModelTier | "all"; this.applyFilter(); }}
                  >${label}</button>
                `;
              }
            )}
          </div>
        </div>

        <div class="billing-modal-body" style="flex: 1; overflow-y: auto; padding-top: var(--space-4);">
          ${this.loading
            ? html`<div style="display: flex; justify-content: center; padding: var(--space-8);"><div class="spinner"></div></div>`
            : this.error
              ? html`<div style="text-align: center; color: var(--red, #ef4444); padding: var(--space-6);">${this.error}</div>`
              : this.filteredModels.length === 0
                ? html`<div style="text-align: center; color: var(--text-tertiary); padding: var(--space-6);">No models found.</div>`
                : html`
                    <div style="display: flex; flex-direction: column; gap: var(--space-2);">
                      ${this.filteredModels.map((m) => this.renderModelRow(m))}
                    </div>
                    ${this.filteredModels.length >= 100
                      ? html`<p style="text-align: center; font-size: var(--text-xs); color: var(--text-tertiary); padding: var(--space-4);">
                          Showing first 100 results. Use search to narrow down.
                        </p>`
                      : nothing}
                  `
          }
        </div>
      </div>
    `;
  }

  private renderModelRow(model: OpenRouterModel): TemplateResult {
    const creditCost = calcCreditCost(model);
    const tier = assignTier(creditCost);
    const tierLabel = TIER_LABELS[tier] ?? tier;
    const isAdded = this.addedIds.has(model.id);
    const contextK = model.context_length
      ? `${Math.round(model.context_length / 1024)}K ctx`
      : "";

    return html`
      <div style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md, 8px);
        border: 1px solid var(--border, #e5e7eb);
        gap: var(--space-3);
      ">
        <div style="flex: 1; min-width: 0;">
          <div style="font-size: var(--text-sm); font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            ${model.name || model.id}
          </div>
          <div style="font-size: var(--text-xs); color: var(--text-tertiary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            ${contextK}
          </div>
        </div>
        <div style="display: flex; align-items: center; gap: var(--space-3); flex-shrink: 0;">
          <span style="
            font-size: var(--text-xs);
            font-weight: 500;
            color: var(--text-secondary);
            background: var(--bg-tertiary);
            padding: 2px 8px;
            border-radius: var(--radius-sm, 4px);
          ">${tierLabel}</span>
          ${isAdded
            ? html`
                <button
                  class="btn btn--danger btn--sm"
                  style="min-width: 72px;"
                  @click=${() => this.handleRemoveModel(model)}
                >
                  Remove
                </button>
              `
            : html`
                <button
                  class="btn btn--primary btn--sm"
                  style="min-width: 72px;"
                  @click=${() => this.handleAddModel(model)}
                >
                  Add
                </button>
              `
          }
        </div>
      </div>
    `;
  }

  private handleAddModel(model: OpenRouterModel): void {
    const creditCost = calcCreditCost(model);
    const tier = assignTier(creditCost);

    const entry: ModelEntry = {
      id: model.id,
      label: model.name || model.id,
      provider: model.id.split("/")[0] ?? "OpenRouter",
      tier,
      creditCost,
    };

    this.addedIds = new Set([...this.addedIds, model.id]);
    this.requestUpdate();

    this.dispatchEvent(
      new CustomEvent("model-add", {
        detail: {
          model: entry,
          contextLength: model.context_length ?? null,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleRemoveModel(model: OpenRouterModel): void {
    const next = new Set(this.addedIds);
    next.delete(model.id);
    this.addedIds = next;
    this.requestUpdate();

    this.dispatchEvent(
      new CustomEvent("model-remove", {
        detail: { modelId: model.id },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleClose(): void {
    this.dispatchEvent(
      new CustomEvent("model-browser-close", { bubbles: true, composed: true })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-model-browser": KatanoidModelBrowser;
  }
}
